import { Component, Injector, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SavingsAccountService } from '@app/finxone-web-frontend/app/lib/services/savings-account-service/savings-account.service';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import {
  handleProcessingStateOnAccount,
  redirectUserToStatusPage,
} from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/savings-account-actions/process-nominated-account.utils';
import { waitForFlutterWebView } from '@app/finxone-web-frontend/app/lib/utils/flutter.utils';
import { getOpenBankingProvider } from '@app/finxone-web-frontend/app/lib/utils/project-settings.utils';
import { AccountErrorCodes } from '@app/translations';
import {
  defaultAispGocardlessFlowRedirectionConfig,
  NominatedAccountStatusEnum,
  OpenBankingProvider,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { catchError, EMPTY, tap } from 'rxjs';

// The query param each provider's own bank-authorisation redirect appends the consent/requisition
// id as - GoCardless (and universal-sandbox, which mirrors it) uses 'ref'; NatWest Payit's
// ResponseURL redirect uses 'c' (ConsentPayitId). Falls back to 'ref' for anything unmapped.
const OPEN_BANKING_REDIRECT_PARAM: Partial<Record<OpenBankingProvider, string>> = {
  [OpenBankingProvider.NATWEST_PAYIT]: 'c',
};

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-nominated-account-status',
  templateUrl: './nominated-account-status.component.html',
})
export class NominatedAccountStatusComponent implements OnInit {
  public isLoading = false;

  constructor(
    private savingsAccountService: SavingsAccountService,
    private store: Store,
    private router: Router,
    private injector: Injector,
  ) {}

  ngOnInit(): void {
    setTimeout(() => {
      this.isLoading = true;
      let requestId = sessionStorage.getItem('requestId');
      waitForFlutterWebView().then((flutterWebView) => {
        if (!flutterWebView || flutterWebView === null) {
          flutterWebView = (window as any).flutter_inappwebview;
        }
        if (!flutterWebView) {
          return;
        }
        flutterWebView?.callHandler('redirectionComplete', []);
      });

      if (!requestId) {
        // try extract from a url param if session storage has failed - which param depends on
        // which open-banking provider is configured, since each provider's own bank-authorisation
        // redirect appends the consent/requisition id under its own convention.
        const openBankingProvider = getOpenBankingProvider(this.store);
        const param = OPEN_BANKING_REDIRECT_PARAM[openBankingProvider] ?? 'ref';

        requestId = new URL(window.location.href).searchParams.get(param) || null;
        console.warn('Request ID not found in session storage, trying URL param', param, requestId);
      }

      if (requestId) {
        this.savingsAccountService
          .processNominatedAccount(requestId)
          .pipe(
            tap((res) => {
              if (res) {
                if (res.status === 'processing') {
                  handleProcessingStateOnAccount(this.injector, requestId);
                }
                // use util method from action button to redirect the user
                redirectUserToStatusPage(res.status, this.store, this.router, res?.accountIds);
              }
            }),
            catchError((err) => {
              console.error(err);
              const { alertCodes = '' } = err.error;
              // take redirection config from project settings state or fallback to default
              const redirectionConfig =
                this.store.selectSnapshot(ProjectSettingsState.getProjectSettings).projectConfig
                  ?.aispFlowRedirectionConfig || defaultAispGocardlessFlowRedirectionConfig;

              if (alertCodes && alertCodes === AccountErrorCodes.NOMINATED_ACCOUNT_MISMATCH) {
                const nominatedAccountMismatchRedirect = redirectionConfig['nominated-account-mismatch'];
                if (nominatedAccountMismatchRedirect?.length)
                  this.router.navigateByUrl(nominatedAccountMismatchRedirect, { replaceUrl: true });
                return EMPTY;
              }

              const url = redirectionConfig[NominatedAccountStatusEnum.ERROR];
              this.router.navigateByUrl(url);
              throw err;
            }),
          )
          .subscribe();
      } else {
        console.error('Request ID not found');
        // take redirection config from project settings state or fallback to default
        const redirectionConfig =
          this.store.selectSnapshot(ProjectSettingsState.getProjectSettings).projectConfig
            ?.aispFlowRedirectionConfig || defaultAispGocardlessFlowRedirectionConfig;

        const url = redirectionConfig[NominatedAccountStatusEnum.ERROR];
        this.router.navigateByUrl(url);
      }
    }, 1000);
  }
}
